import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row, Button } from 'react-bootstrap';

import { updateCertificationStatusById } from 'api/certifications/certifications';
import { certificationActions } from 'store/slices/certification/certificationSilce';
import { getCatalog, handleApiError } from 'utils';

import styles from 'assets/scss/wizard/emetBreak.module.scss';

/**
 * @component Welcome
 * @description
 * Componente de bienvenida al examen. Muestra un mensaje con el tipo de certificación
 * y un botón para comenzar el examen. Al iniciar:
 * - Actualiza el estado de la certificación a "en progreso" (status: 1).
 * - Avanza al primer paso del wizard del examen.
 *
 * @param {Object} props
 * @param {Object} props.certification - Objeto de certificación con `id` y `certification_type`.
 *
 * @returns {JSX.Element}
 *
 * @example
 * <Welcome certification={{ id: 1, certification_type: 'EPST' }} />
 */
const Welcome = ({ certification }) => {
	const token = useSelector(state => state.authentication.user?.token);
	const dispatch = useDispatch();

	/**
	 * Inicia el examen: actualiza el estado de la certificación y avanza al siguiente paso.
	 */
	const handleStartExam = () => {
		updateCertificationProgress();
		dispatch(certificationActions.incrementStep());
	};

	/**
	 * Actualiza el estado de la certificación a "En progreso" (status: 1) mediante la API.
	 */
	const updateCertificationProgress = async () => {
		try {
			await updateCertificationStatusById(certification.id, { status: 1 }, token);
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Renderiza el contenido visual del mensaje de bienvenida y el botón para comenzar.
	 *
	 * @param {Function} onStart - Función a ejecutar al hacer clic en "Comenzar".
	 * @returns {JSX.Element}
	 */
	const renderWelcomeContent = (onStart) => (
		<Row className="align-items-center justify-content-center mt-5">
			<Col lg={12} className="text-center">
				<h2 className={`${styles.welcomeMessage} mb-4`}>
					¡Bienvenido al Examen de Certificación {getCatalog(certification.certification_type)}!
				</h2>
				<p className={`${styles.instructions} mb-5`}>
					Haz clic en el botón a continuación para comenzar el examen. Te recomendamos que te prepares antes de iniciar.
				</p>
				<Button
					variant="primary"
					size="lg"
					onClick={onStart}
					className={styles.startButton}>
					Comenzar
				</Button>
			</Col>
		</Row>
	);

	return renderWelcomeContent(handleStartExam);
};

export default Welcome;